import { cn } from "@/lib/utils";
import Link from "next/link";
import React from "react";

interface FancyButtonProps {
  onClick?: () => void;
  href?: string;
  semiFancy?: boolean;
  inNewTab?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

const FancyButton: React.FC<FancyButtonProps> = ({
  onClick,
  semiFancy = false,
  inNewTab = false,
  href,
  children,
  disabled = false,
  className,
  size = "md",
}) => {
  const sizeClasses = {
    sm: "px-2.5 py-1.5 text-xs",
    md: "px-3.5 py-2 text-sm",
    lg: "px-4 py-2.5 text-base",
    xl: "px-5 py-3 text-lg",
  };

  const sharedClassName = cn(
    // Base classes
    "flex items-center justify-center overflow-hidden leading-[normal] tracking-[normal] text-white transition duration-200 ease-in-out",
    // Conditional styling based on semiFancy
    semiFancy
      ? "w-full gap-2.5 rounded-[12px] py-3 [background:linear-gradient(180deg,_rgba(255,_255,_255,_0.16)_0%,_rgba(255,_255,_255,_0)_100%),_#335CFF] md:h-11 md:py-3"
      : cn(
          "border-linear-12 flex-row rounded-full shadow-[0px_1px_2px_rgba(14,_18,_27,_0.24),_0px_0px_0px_1px_#5B7CFF] [background:linear-gradient(180deg,_rgba(255,_255,_255,_0.37),_rgba(255,_255,_255,_0)),_#3860FF]",
          sizeClasses[size],
        ),
    disabled
      ? "cursor-not-allowed md:opacity-50 md:hover:opacity-50"
      : "cursor-pointer md:hover:opacity-90",
    className,
  );

  if (href) {
    return (
      <Link
        href={href}
        className={sharedClassName}
        target={inNewTab ? "_blank" : undefined}
        rel={inNewTab ? "noopener noreferrer" : undefined}
      >
        {children}
      </Link>
    );
  }

  return (
    <button className={sharedClassName} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

export default FancyButton;
